/**
 * @module support/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

import { Injectable } from '@angular/core';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ProductSelectorLandingModel } from '@experience/shop/product-selector/models/product-selector-landing.model';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import {
  LineDeivceDetails,
  lineTypeEnum,
  TileIdAndLengthToCreateLine,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { Cart, CartLine } from '@support/api/proxies/responses/api-cart-response.interface';
import { ByodDeviceData } from '@experience/shop/byod/interfaces/byod-landing.interface';
import { Subject, Observable } from 'rxjs';
import { ItemDetail } from '@support/api/common/product.interface';

@Injectable(
  { providedIn: 'root' }
)
export class TileManagementService {
  /**
   * Holds current tile ID details
   */
  public focusedTileDetails: Subject<LineDeivceDetails>;
  public getLineNonLineItemDetails: Subject<LineDeivceDetails[]>;
  private productSelectorLandingModel: ProductSelectorLandingModel;

  /**
   * Subject declaration
   */
  public selectedLineDetailsSource: Subject<boolean>;
  /**
   * Observable declaration
   */
  public selectedLineDetailsSpinner$: Observable<boolean>;

  constructor(
    private stateStoreUtilService: StateStoreUtilsService,
    private objectService: ObjectService,
    private commonUtilService: CommonUtilService
  ) {
    this.focusedTileDetails = new Subject<LineDeivceDetails>();
    this.getLineNonLineItemDetails = new Subject<LineDeivceDetails[]>();
    this.stateStoreUtilService.getFocusedTileLineNonLineItem();
    this.productSelectorLandingModel = new ProductSelectorLandingModel(
      this.objectService,
      this.stateStoreUtilService
    );
    this.selectedLineDetailsSource = new Subject();
    this.selectedLineDetailsSpinner$ = this.selectedLineDetailsSource.asObservable();
  }

  /**
   * Function to create new tile
   * @param {lineTypeEnum} lineType
   * @param {boolean} noPlanNeeded
   * @param existingLines
   * @param deviceDetails
   */
  public createTile(
    lineType: lineTypeEnum = lineTypeEnum.LINE,
    noPlanNeeded: boolean = false,
    existingLines?: CartLine,
    deviceDetails?: ItemDetail,
    existingTileDetails?: TileIdAndLengthToCreateLine
  ): void {
    let tileDetails: TileIdAndLengthToCreateLine = existingTileDetails
      ? existingTileDetails
      : this.generateTile(lineType);
    this.productSelectorLandingModel.createTile(
      tileDetails,
      lineType,
      noPlanNeeded,
      existingLines,
      deviceDetails
    );
  }

  /**
   * Function to get focused tile details
   */
  public getFocusedTileIdDetails(): LineDeivceDetails {
    return this.stateStoreUtilService.getFocusedTileLineNonLineItem();
  }

  /**
   * Function to set tile in focus in model and state store service;
   */
  public setTileIdInFocus(tileId: string): void {
    this.stateStoreUtilService.setFocusedTileId(tileId);
  }

  /**
   * Function to get tile in focus in model and state store service; L001, L002..
   */
  public getTileIdInFocus(): string {
    return this.stateStoreUtilService.getFocusedTileId();
  }

  /**
   * Function to map lines with cart response(2109)
   * @param {Cart} cartResponse
   */
  public mapToCartResponse(cartResponse: Cart): void {
    this.productSelectorLandingModel.getPlansFromCartResponse(cartResponse);
  }

  /**
   * Sets to true if no plan needed is selected in PS page
   * @param {boolean} noPlanNeeded
   */
  public setIsPlanNeeded(noPlanNeeded: boolean): void {
    this.productSelectorLandingModel.setIsPlanNeeded(noPlanNeeded);
  }

  /**
   * Function that returns details for all lines
   * @returns {LineDeivceDetails[]}
   */
  public getAllLineDetails(): void {
    const allLineItemDetails: LineDeivceDetails[] = [];
    const lineItemTileIdList = this.stateStoreUtilService.getTileIdList(lineTypeEnum.LINE);
    const NonLineItemTileIdList = this.stateStoreUtilService.getTileIdList(lineTypeEnum.NONLINE);
    const tempListOfTileId = lineItemTileIdList.concat(NonLineItemTileIdList);
    const cachedTileIdList = this.stateStoreUtilService.getTileIdList(null, true);
    const cacheData = this.stateStoreUtilService.getFocusedTileLineNonLineItem(null, true);
    tempListOfTileId.forEach((tileId: string) => {
      // Following to get all lines from cache
      const tileIdDetails = this.objectService.get(
        cacheData,
        `${tileId}.lineNonLineItem.activation`
      );
      allLineItemDetails.push(tileIdDetails);
    });
    this.getLineNonLineItemDetails.next(allLineItemDetails);
  }

  /**
   * Function to store selected sim kit in BYOD screen
   * @param {NewSimData} simDetails
   * @param {ByodDeviceData} byodModel
   */
  public setSelectedSimDetails(byodModel: ByodDeviceData, simDetails?: string): void {
    this.productSelectorLandingModel.setSimDetails(byodModel, simDetails);
  }
  /**
   * Function used to check type of focused tile
   * @returns {boolean}
   */
  public isFocusedTileNonLineItem(): boolean {
    const nonLineItemList = this.stateStoreUtilService.getTileIdList(lineTypeEnum.NONLINE);
    return nonLineItemList.indexOf(this.stateStoreUtilService.getFocusedTileId()) > -1;
  }

  /**
   * Function used to map to non line items
   * @param {Cart} nonLineItem
   * @param {ItemDetail} productOffers
   * @returns void
   */
  public setNonLineItemDetails(nonLineItem: Cart, productOffers?: ItemDetail): void {
    this.productSelectorLandingModel.mapToNonLineItemDevices(nonLineItem, productOffers);
  }

  /**
   * Function to delete line
   * @returns void
   */
  public deleteLine(): void {
    this.stateStoreUtilService.clearFocusedTileIdDetails(
      this.stateStoreUtilService.getFocusedTileId()
    );
    if (this.isFocusedTileNonLineItem()) {
      this.productSelectorLandingModel.deleteLine(lineTypeEnum.NONLINE);
    } else {
      this.productSelectorLandingModel.deleteLine(lineTypeEnum.LINE);
    }
  }

  // ----------------- Total Line and Non line item Counts   -----------
  public getUpdatedData(actionCode?: boolean): void {
    const tempTileDetails: LineDeivceDetails =
      this.stateStoreUtilService.getFocusedTileLineNonLineItem();
    if (actionCode) {
      tempTileDetails.getUpdatedPlans = actionCode;
    }
    this.focusedTileDetails.next(tempTileDetails);
    this.getAllLineDetails();
  }

  /* Function used to generate Tile Id
   * @param {lineTypeEnum} type
   * @param {boolean} isTempTile
   * @returns {string}
   */
  public generateTile(
    type: lineTypeEnum,
    isTempTile: boolean = false
  ): TileIdAndLengthToCreateLine {
    type = type === lineTypeEnum.EXISTING ? lineTypeEnum.LINE : type;
    let existingTilesList = [];
    let newLastItemListElement;
    existingTilesList = this.stateStoreUtilService.getTileIdList(type);
    if (existingTilesList.length > 0) {
      const lastLineItemElement = existingTilesList[existingTilesList.length - 1];
      const lineItemListElement = lastLineItemElement.slice(1);
      newLastItemListElement = (+lineItemListElement + 1).toString();
    }
    const newTileId = this.commonUtilService.generateLineNumber(newLastItemListElement, type);
    if (!isTempTile) {
      this.stateStoreUtilService.setFocusedTileId(newTileId);
      existingTilesList.push(newTileId);
      this.stateStoreUtilService.setTileIdList(existingTilesList, type);
    }
    return {
      tileId: newTileId,
      currentLength: existingTilesList.length,
    };
  }

  /**
   * Function to get all active tiles
   * @returns array of string, returns all active tile ids
   */
  public getAllTileId(): string[] {
    return this.stateStoreUtilService
      .getTileIdList(lineTypeEnum.LINE)
      .concat(this.stateStoreUtilService.getTileIdList(lineTypeEnum.NONLINE));
  }

  /**
   * get tile in focus on the basis of existing line LoS ID
   * @returns {string} tileId
   */
  public getExisitingLineTileId(lineId: string): string {
    const allLineItemDetails: LineDeivceDetails[] = [];
    const lineItemTileIdList = this.stateStoreUtilService.getTileIdList(lineTypeEnum.LINE);
    lineItemTileIdList.forEach((tileId: string) => {
      const details: LineDeivceDetails =
        this.stateStoreUtilService.getFocusedTileLineNonLineItem(tileId);
      allLineItemDetails.push(details);
    });
    const tileInFocus = allLineItemDetails.find((line: LineDeivceDetails) => {
      return line.lineId === lineId;
    });
    return this.objectService.get(tileInFocus, 'tileId');
  }
}
